/**
 * 用于拖拉拽页面的 表结构和表数据预览弹窗的自定义hook
 */
import React, {useMemo, useState} from "react";
import { useRequest } from "ahooks";
import { getTableInfoList, getTablePreviewData } from '@dbl/services/dataSource';
import { useIntl, shallowEqual, useSelector } from "umi";
import { LANGUAGE_EN } from "@home/_config/config";
import {TableColumn, Bubble, Text} from "tea-component";
import type { ITableColumnDto } from '@dbl/services/dataSource';
import type { IAnalysisCustomColumn } from '@dbl/services/chart';
import { FieldType } from '@dbl/core/dataSource';
import {
  DimensionType,
  DimensionValue,
  Include255,
} from '@dbl/core/dataSource';
import {getLocalText} from "@/pages/datalab/utils/utils";
import { ITableRecord, ITableDataState } from './types';

// Hook 相关的类型定义
interface IUseDragDropPageDetailProps {
  isSingle?: boolean;
  // curCustomColumns?: IAnalysisCustomColumn[];
}

// 表信息类型
interface ITableInfo {
  connect_type: string;
  customize_name: string;
  customize_name_en: string;
  granularity: string;
  on: unknown[];
  table_code: string;
  table_id: string;
  table_name: string;
}

// 打开模态框参数类型
interface IOpenModalParams {
  tableIds: string[];
  tableInfos: ITableInfo[];
  dataTables: IDataTable[];
  customColumns?: IAnalysisCustomColumn[];
  resetProps?: {
    title?: React.ReactNode | string;
    isSingle?: boolean;
  };
}

// 数据表类型
interface IDataTable {
  table_id: string;
  table_code: string;
  driver: string;
  database_uuid: string;
  kind?: string; // SQL View模式下要传递
}

// Hook 返回值类型
interface IUseDragDropPageDetailReturn {
  title?: string;
  visible: boolean;
  struct: ITableDataState;
  preview: ITableDataState;
  openModal: (params: IOpenModalParams) => void;
  onClose: () => void;
}

const useDragDropPageDetail = (props: IUseDragDropPageDetailProps): IUseDragDropPageDetailReturn => {
  const { isSingle: isSingleTable } = props;
  const { gameCode } = useSelector((state: { datalab?: { gameCode: string; }; }) => ({
    gameCode: state?.datalab?.gameCode,
  }), shallowEqual);
  const { formatMessage, locale } = useIntl();
  // 检测当前语言
  const isEn = locale === LANGUAGE_EN;
  
  // Modal 是否显示
  const [visible, setVisible] = useState<boolean>(false);
  // Detail弹窗组件的props
  const [resetDetailProps, setResetDetailProps] = useState<{ title?: string; }>({});
  // 是否为单表展示
  const [isSingle, setIsSingle] = useState<boolean>(isSingleTable ?? false);
  
  // 表结构列配置
  const structColumns = useMemo((): TableColumn[] => {
    const columns = [
      {
        key: 'display_name',
        header: formatMessage({ id: 'dbl_personal_data_display_name' }),
        width: 185,
        render(record: ITableColumnDto) {
          // 在useMemo中可以使用React组件或html元素
          return (
            <div>
              {(isEn ? record.display_name_en : record.display_name) || record.display_name || record.name || '-'}
            </div>
          );
        },
      },
      {
        key: 'name',
        header: formatMessage({ id: 'dbl_personal_data_field_name' }),
        width: 185,
        render(record: ITableColumnDto) {
          return <div>{record.name || '-'}</div>;
        },
      },
      {
        key: 'table_name',
        header: formatMessage({ id: 'dbl_personal_data_table_name' }),
        width: 185,
        render(record: ITableColumnDto) {
          return <div>{record.table_name || '-'}</div>;
        },
      },
      {
        key: 'type',
        header: formatMessage({ id: 'dbl_personal_data_field_type' }),
        width: 120,
        render(record: ITableColumnDto) {
          if (Object.values(FieldType).includes(record.type)) {
            // 映射 FieldType 到对应的国际化键
            const typeKeyMap: Record<string, string> = {
              [FieldType.Date]: 'dbl_multi_date',
              [FieldType.String]: 'dbl_multi_string',
              [FieldType.Number]: 'dbl_multi_number',
              [FieldType.Float]: 'dbl_multi_float',
              [FieldType.Percent]: 'dbl_multi_percent',
            };
            const messageKey = typeKeyMap[record.type];
            return messageKey ? formatMessage({ id: messageKey }) : record.type;
          }
          return '-';
        },
      },
      {
        key: 'attribute',
        header: <div>{formatMessage({ id: 'dbl_personal_data_metric_dimension' })}</div>,
        width: 130,
        render(record: ITableColumnDto) {
          return record.is_metric ? formatMessage({ id: 'dbl_personal_data_metric' }) : formatMessage({ id: 'dbl_personal_data_dimension' });
        },
      },
      {
        key: 'dimension_setting',
        header: <div>{formatMessage({ id: 'dbl_personal_data_dimension_value_settings' })}</div>,
        width: 140,
        render(record: ITableColumnDto) {
          if (record.attribute === DimensionType.Metric) {
            return '-';
          }
          return record.dimension_setting === DimensionValue.Origin
            ? formatMessage({ id: 'dbl_personal_data_origin' })
            : formatMessage({ id: 'dbl_personal_data_system' });
        },
      },
      {
        key: 'flag',
        header: <div>{formatMessage({ id: 'dbl_personal_data_include_dimension_total_value' })}</div>,
        width: 160,
        render(record: ITableColumnDto) {
          if (record.attribute === DimensionType.Metric) {
            return '-';
          }
          return record.flag === Include255.Include
            ? formatMessage({ id: 'dbl_personal_data_include' })
            : formatMessage({ id: 'dbl_personal_data_exclude' });
        },
      },
      {
        key: 'remark',
        width: 240,
        header: formatMessage({ id: 'dbl_personal_data_field_desc' }),
        render(record: ITableColumnDto) {
          return <div>{getLocalText(isEn, record.remark, record.remark_en) || '-'}</div>;
        },
      },
    ];
  
    if (isSingle) {
      // 展示单表的结构详情时, 不需要展示 table_name 列
      columns.splice(2, 1);
    }
    return columns;
  }, [isEn, isSingle]);
  
  // 获取数据表 结构的请求
  const {
    data: structData,
    loading: structLoading,
    error: structError,
    run: runGetTableStruct,
  } = useRequest(async (tableIds: string[], gameCode: string) => {
    const data = await getTableInfoList(tableIds, gameCode) as unknown as { table_name: string; column_list: ITableColumnDto[]; }[];
    const allData = data?.reduce((acc: ITableRecord[], table) => {
      const tableName = getLocalText(isEn, table?.customize_name, table?.customize_name_en, table?.table_name);
      const list = table?.column_list?.map((item: ITableColumnDto) => ({...item, table_name: tableName})) || [];
      return acc.concat(list);
    }, []) || [];
    
    // 自定义字段不上传, 也不返回详情
    // if (curCustomColumns?.length > 0) {
    //   curCustomColumns?.forEach(item => {
    //     allData.push({
    //       display_name: item?.name_cn,
    //       display_name_en: item?.name_en,
    //       name: item?.name_en,
    //       table_name: '', // 计算/Calculation
    //       type: item?.column_type,
    //       attribute: '',
    //       dimension_setting: '',
    //       flag: '',
    //       remark: item.remark,
    //     });
    //   });
    // }
    return allData;
  }, { manual: true });
  
  // 获取数据表 预览数据的请求
  const {
    data: previewData,
    loading: previewLoading,
    error: previewError,
    run: runGetTablePreview,
  } = useRequest((params: {
    game_code: string;
    table_infos: ITableInfo[];
    data_tables: IDataTable[];
    // custom_columns: IAnalysisCustomColumn[];
    isSingle: boolean;
  }) => {
    const {isSingle, ...restParams} = params;
    return getTablePreviewData(restParams).then((data: any) => {
      // 展示表格的时候, 中英文是固定的,需要关闭弹窗后再切换中英文, 打开弹窗后 会重新走这里的逻辑,
      const columns = data?.column_name?.map((column: { name: string; is_sensitive: boolean; table_name: string; }) => {
        const tableName = isSingle ? '' : getLocalText(isEn, column?.customize_name, column?.customize_name_en, column?.table_name);
        
        let newColumn: TableColumn = {
          key: column.table_name + column.name,
          // 展示自定义字段时, 没有多表情况, 表头不需要展示中英文
          header: isSingle ? column.name : (<div>
            <Text parent="div" theme="label" title={column.table_name} style={{fontSize: '12px', fontWeight: '400', color: '#838697'}} className={'dbl-text-overflow'}>{tableName}</Text>
            <br/>
            <Text parent="div" title={column.name} overflow>{column.name}</Text>
          </div>), // 多表时, name上方展示表名
          width: 200,
          render: (record) => record[column.name] || ''
        };
        if (column.is_sensitive) {
          newColumn = {
            ...newColumn,
            render: () => (
              <div>
                <Bubble dark content={'not_show'} placement={'top'}>
                  ***
                </Bubble>
              </div>
            ),
          };
        }
        return newColumn;
      }) || [];
      
      return { columns, records: data?.table_data || []};
    });
  }, { manual: true });
  
  
  const openModal = ({tableIds, tableInfos, dataTables, resetProps }: IOpenModalParams) => {
    // 打开弹窗时, 传递Detail弹窗一些参数
    resetProps?.title && setResetDetailProps({title: resetProps.title});
    // 打开弹窗时, 设定是单表展示, 还是多表展示
    (typeof resetProps?.isSingle === 'boolean') && setIsSingle(resetProps.isSingle);
    
    // 发起 表结构 请求
    runGetTableStruct(tableIds, gameCode || '');
    
    // 发起 表预览数据 请求
    runGetTablePreview({
      game_code: gameCode || '',
      table_infos: tableInfos,
      data_tables: dataTables,
      // custom_columns: customColumns || [],
      isSingle: (typeof resetProps?.isSingle === 'boolean') ? resetProps.isSingle : isSingle,
    });
    
    // 打开弹窗
    setVisible(true);
  };
  
  return {
    ...resetDetailProps,
    visible,
    struct: {
      loading: structLoading,
      error: structError,
      records: structError ? [] : (structData || []),
      columns: structColumns,
    },
    preview: {
      loading: previewLoading,
      error: previewError,
      records: previewError ? [] : (previewData?.records || []),
      columns: previewError ? [] : (previewData?.columns || []),
    },
    openModal,
    onClose: () => setVisible(false),
  };
};

export default useDragDropPageDetail;